/**
 * Authentication Migration Helper
 * 
 * Utility for checking and managing migration status.
 * After migration is complete, this can be simplified or removed.
 */

import { hasOldTokens, performMigrationCleanup, isMigrationComplete } from './tokenCleanup';
import { logError } from './errorLogger';

/**
 * Migration status
 */
export interface MigrationStatus {
  needsMigration: boolean;
  migrationComplete: boolean;
  hasOldTokens: boolean;
}

/**
 * Check migration status for current user
 */
export function checkMigrationStatus(): MigrationStatus {
  const migrationComplete = isMigrationComplete();
  const hasOld = hasOldTokens();
  
  return {
    needsMigration: hasOld && !migrationComplete,
    migrationComplete,
    hasOldTokens: hasOld,
  };
}

/**
 * Trigger migration cleanup
 * Should be called after successful login
 */
export function triggerMigrationCleanup(): void {
  try {
    performMigrationCleanup();
  } catch (error) {
    logError('Migration cleanup failed', error);
  }
}

/**
 * Migrate user data while preserving preferences
 * This ensures user preferences are maintained during migration
 */
export function migrateUserPreferences(): {
  success: boolean;
  preferencesMigrated: string[];
} {
  const preferencesMigrated: string[] = [];
  
  try {
    // Migrate remember me preference
    const rememberMe = localStorage.getItem('mirsal_remember_me');
    if (rememberMe) {
      // Already in localStorage, no migration needed
      preferencesMigrated.push('remember_me');
    }
    
    // Migrate last activity
    const lastActivity = localStorage.getItem('mirsal_last_activity');
    if (lastActivity) {
      preferencesMigrated.push('last_activity');
    }
    
    // Migrate user data (non-sensitive)
    const userData = localStorage.getItem('mirsal_user_data') || sessionStorage.getItem('mirsal_user_data');
    if (userData) {
      preferencesMigrated.push('user_data');
    }
    
    return {
      success: true,
      preferencesMigrated,
    };
  } catch (error) {
    logError('Failed to migrate user preferences', error);
    return {
      success: false,
      preferencesMigrated,
    };
  }
}

/**
 * Perform complete migration for existing user
 * This should be called once after login for users with old tokens
 */
export function performUserMigration(): {
  success: boolean;
  cleanupPerformed: boolean;
  preferencesMigrated: boolean;
} {
  const status = checkMigrationStatus();
  
  if (!status.needsMigration) {
    return {
      success: true,
      cleanupPerformed: false,
      preferencesMigrated: false,
    };
  }
  
  try {
    // Migrate preferences first
    const prefsResult = migrateUserPreferences();
    
    // Then perform cleanup
    triggerMigrationCleanup();
    
    return {
      success: true,
      cleanupPerformed: true,
      preferencesMigrated: prefsResult.success,
    };
  } catch (error) {
    logError('User migration failed', error);
    return {
      success: false,
      cleanupPerformed: false,
      preferencesMigrated: false,
    };
  }
}

